import React, { useCallback, useState, useEffect, useMemo } from 'react';
import TextField, { TextFieldProps } from '@material-ui/core/TextField';
import InputAdornment from '@material-ui/core/InputAdornment';
import { FieldProps } from 'formik';
import { useValueStyles } from './styles';
import { useTranslation } from 'react-i18next';
import { withField } from './withFields';
import { getErrorMessageKey } from 'helpers/helpers';
import withInfo from 'components/Tooltip/withInfo';
import NumberFormat, { NumberFormatProps } from 'react-number-format';

interface OwnProps {
  name: string;
  afterBlur?: () => void;
  afterChange?: () => void;
  specificError?: boolean;
  endAdornment?: string;
  readOnly?: boolean;
  numberFormatProps?: NumberFormatProps;
  maxLength?: number;
  thousandSeparator?: boolean;
  decimalScale?: number;
}

type Props = OwnProps & TextFieldProps & FieldProps;

/**
 * Wrapper around MuiTextField ready to be wrapped with Formik's Field or FastField
 *
 * @param name {string} - name of the input needed for the formik's fields so it can hook it to the form
 * @param afterBlur {function} - optional parameter that tell the component to display specific error from backend instead of generic one
 * @param specificError {boolean} - any material-ui TextField props can be applied
 * @param endAdornment {string} - formik props so Formik can do it's things, they are added automaticaly via formiks Field
 * @param readOnly {boolean}
 */
const CustomFormNumberField: React.FunctionComponent<Props> = React.memo(
  ({
    form, // from formik
    field, // from formik
    meta, // from formik
    afterBlur,
    afterChange,
    specificError,
    InputProps,
    endAdornment,
    readOnly,
    InputLabelProps,
    numberFormatProps,
    error,
    maxLength,
    thousandSeparator,
    decimalScale,
    ...rest
  }) => {
    const classes = useValueStyles();
    const { t } = useTranslation();
    const { name, value } = field;

    const [afterFocusValue, setAfterFocusValue] = useState(0);
    const [callAfterBlur, setCallAfterBlur] = useState(false);
    const [callAfterChange, setCallAfterChange] = useState(false);
    const [firstRender, setFirstRender] = useState(true);

    const enhancedInputProps: typeof InputProps = useMemo(
      () => ({
        readOnly,
        endAdornment: endAdornment && (
          <InputAdornment position="end">{endAdornment}</InputAdornment>
        ),
        classes: {
          adornedEnd: readOnly ? classes.readOnly : '',
        },
        ...InputProps,
        inputComponent: NumberFormatCustom as any,
        inputProps: { maxLength: maxLength || 14, thousandSeparator, decimalScale, ...numberFormatProps },
      }),
      [
        InputProps,
        classes.readOnly,
        endAdornment,
        maxLength,
        numberFormatProps,
        readOnly,
        thousandSeparator,
        decimalScale
      ],
    );

    const inputLabelProps: typeof InputLabelProps = useMemo(
      () => ({
        ...InputLabelProps,
        classes: {
          outlined: readOnly ? classes.outlined : '',
        },
      }),
      [InputLabelProps, classes.outlined, readOnly],
    );

    // When the field is created with empty value
    // set it's value to zero.
    useEffect(() => {
      if (firstRender && !value) {
        form.setFieldValue(name, 0);
        setFirstRender(false);
      }
    }, [firstRender, form, name, value]);

    // If there is afterChange callback
    // and the field value changed, call it.
    useEffect(() => {
      if (callAfterBlur) {
        if (`${value}` !== `${afterFocusValue}`) {
          afterBlur?.();
        }
        setCallAfterBlur(false);
      }
    }, [afterFocusValue, callAfterBlur, value, afterBlur]);

    // If there is an error, translate it's key, else make it empty.
    const errorMessage = useMemo(
      () => (meta.error ? t(getErrorMessageKey(meta.error, specificError)) : ''),
      [meta.error, specificError, t],
    );

    // when the value is changed and we want to show intialValues show them
    const note = useMemo(
      () =>
        form.status?.showInitialValues && meta.initialValue !== field.value
          ? `Původní hodnota: ${meta.initialValue}`
          : '',
      [field.value, form.status, meta.initialValue],
    );

    const onBlur = useCallback(
      (e: React.FocusEvent<any>) => {
        // If there is no value on blur, set it to zero
        if (value === '') {
          form.setFieldValue(name, 0);
        }

        // If there is afterChange callback, set the flag
        // which informs the component to call the callback after
        // it do it's own onBlur and rerender with new props
        if (afterBlur) {
          setCallAfterBlur(true);
        }
        field.onBlur(e);
      },
      [afterBlur, field, form, name, value],
    );

    // save value of the field, so it can be compared
    const onFocus = useCallback(() => {
      setAfterFocusValue(value);
      if (`${value}` === '0') {
        form.setFieldValue(name, '');
      }
    }, [value, form, name]);

    useEffect(() => {
      if (callAfterChange) {
        afterChange?.();
        setCallAfterChange(false);
      }
    }, [callAfterChange, afterChange]); //field.value

    const onChange = useCallback(
      (e: React.FocusEvent<any>) => {
        if (afterChange) {
          setCallAfterChange(true);
        }
        field.onChange(e);
      },
      [afterChange, field],
    );

    return (
      <TextField
        size="small"
        className={classes.number}
        {...field}
        onBlur={onBlur}
        onFocus={onFocus}
        onChange={onChange}
        error={!!errorMessage || error}
        helperText={errorMessage || note}
        {...rest}
        InputProps={enhancedInputProps}
        InputLabelProps={inputLabelProps}
      />
    );
  },
);

const FormNumberField = withField(withInfo(CustomFormNumberField));

// MASK BELLOW

interface NumberFormatCustomProps {
  inputRef: (instance: NumberFormat | null) => void;
  onChange: (event: { target: { name: string; value: string } }) => void;
  name: string;
  thousandSeparator?: boolean;
  decimalScale?: number;
  percentage?: boolean;
}

const NumberFormatCustom = React.memo((props: NumberFormatCustomProps) => {
  const { inputRef, onChange, thousandSeparator, decimalScale, percentage, ...rest } = props;

  return (
    <NumberFormat
      getInputRef={inputRef}
      onValueChange={({ value }) => {
        onChange({
          target: {
            name: props.name,
            value,
          },
        });
      }}
      thousandSeparator={thousandSeparator !== false ? ' ' : undefined}
      decimalSeparator=","
      isNumericString
      allowNegative={false}
      decimalScale={decimalScale || 1}
      {...rest}
    />
  );
});

export default FormNumberField;
